import type { PropertySectionKey } from 'components/reports/propertyReportSections/propertySectionKeys';

/**
 * Maps V2 property-report nav item `name`s to `PropertySectionKey`s so the
 * sidebar can follow the same order as the report body (UPS-1263).
 *
 * Parent groups are ranked by their earliest mapped child. Items with no
 * mapping (e.g. Crime Incidents) keep their original relative position.
 */
export const PROPERTY_NAV_NAME_TO_SECTION_KEY: Record<
  string,
  PropertySectionKey
> = {
  overview: 'overview',
  'property-details': 'property_details',
  taxes: 'assessed_value_and_taxes',
  'know-your-neighborhood': 'know_your_neighborhood',
  'building-permits': 'building_permits',
  hoa: 'home_owner_association',
  'valuation-history': 'property_valuation',
  'equity-&-loans': 'property_valuation',
  'possible-owners': 'possible_owners',
  'possible-residents': 'residents',
  deeds: 'deeds',
  liens: 'property_liens',
  'market-insights': 'market_insights',
  'sex-offenders': 'sex_offenders',
  demographics: 'property_demographics',
  schools: 'property_schools',
  'natural-hazards': 'natural_hazards',
  'member-perks': 'perks_banner',
  attachments: 'pdf',
  notes: 'notes',
  'related-reports': 'related_reports',
  'report-rating': 'report_rating',
};

export interface SortableNavItem {
  name?: string;
  childItems?: SortableNavItem[];
}

const mappedSectionRank = (
  name: string | undefined,
  orderIndex: ReadonlyMap<PropertySectionKey, number>
): number | null => {
  if (!name) {
    return null;
  }

  const sectionKey = PROPERTY_NAV_NAME_TO_SECTION_KEY[name];
  if (!sectionKey) {
    return null;
  }

  const rank = orderIndex.get(sectionKey);
  return rank === undefined ? null : rank;
};

const itemMappedRank = <T extends SortableNavItem>(
  item: T,
  orderIndex: ReadonlyMap<PropertySectionKey, number>
): number | null => {
  const ownRank = mappedSectionRank(item.name, orderIndex);

  if (!item.childItems?.length) {
    return ownRank;
  }

  const childRanks = item.childItems
    .map((child) => itemMappedRank(child, orderIndex))
    .filter((rank): rank is number => rank !== null);

  const childMin = childRanks.length > 0 ? Math.min(...childRanks) : null;

  if (ownRank !== null && childMin !== null) {
    return Math.min(ownRank, childMin);
  }

  return ownRank ?? childMin;
};

/**
 * Fill gaps so unmapped siblings keep their original relative position
 * between mapped neighbors instead of jumping to the end of the list.
 */
const interpolateRanks = (ranks: Array<number | null>): number[] => {
  const defined = ranks
    .map((rank, index) => (rank === null ? null : { index, rank }))
    .filter(
      (entry): entry is { index: number; rank: number } => entry !== null
    );

  if (defined.length === 0) {
    return ranks.map((_, index) => index);
  }

  return ranks.map((rank, index) => {
    if (rank !== null) {
      return rank;
    }

    const previous = defined.findLast((entry) => entry.index < index);
    const next = defined.find((entry) => entry.index > index);

    if (previous && next) {
      const progress = (index - previous.index) / (next.index - previous.index);
      return previous.rank + progress * (next.rank - previous.rank);
    }

    if (previous) {
      return previous.rank + (index - previous.index) * 0.001;
    }

    if (next) {
      return next.rank - (next.index - index) * 0.001;
    }

    return index;
  });
};

/**
 * Recursively sorts nav items (and their `childItems`) to match
 * `sectionOrder`. Stable for equal ranks so default order is unchanged
 * when `sectionOrder` is `PROPERTY_REPORT_SECTION_ORDER_DEFAULT`.
 */
export const sortNavItemsBySectionOrder = <T extends SortableNavItem>(
  items: T[],
  sectionOrder: readonly PropertySectionKey[]
): T[] => {
  const orderIndex = new Map(
    sectionOrder.map((sectionKey, index) => [sectionKey, index])
  );

  const withSortedChildren = items.map((item) => {
    if (!item.childItems?.length) {
      return item;
    }

    return {
      ...item,
      childItems: sortNavItemsBySectionOrder(item.childItems, sectionOrder),
    };
  });

  const sortKeys = interpolateRanks(
    withSortedChildren.map((item) => itemMappedRank(item, orderIndex))
  );

  return withSortedChildren
    .map((item, originalIndex) => ({
      item,
      originalIndex,
      sortKey: sortKeys[originalIndex] ?? originalIndex,
    }))
    .sort((left, right) => {
      if (left.sortKey !== right.sortKey) {
        return left.sortKey - right.sortKey;
      }

      return left.originalIndex - right.originalIndex;
    })
    .map(({ item }) => item);
};
